/**
 * Purchase Advice Upload Service
 *
 * Shared business logic for uploading Purchase Advice (PA) documents.
 * Used by both Banker Recon InvestorAdviceTab and Post Closing InvestorDeliveryTab.
 *
 * Handles: storage upload → loan_documents insert → document_processing_queue insert.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { reserveLoanStorageObject } from '@/services/loanStorageAuthorizationService';

const log = createComponentLogger('PurchaseAdviceUploadService', 'services');

export interface PAUploadParams {
  file: File;
  organizationId: string;
  encompassLoanId: string;
  userId: string;
  uploadContext: 'post_closing' | 'banker_recon';
  /** If a banker recon exists for this loan, link the document to it */
  bankerReconId?: string | null;
}

export interface PAUploadResult {
  docId: string;
  storagePath: string;
  createdAt: string;
}

/**
 * Upload a Purchase Advice PDF to storage and create tracking records.
 *
 * 1. Uploads file to `loan-documents` bucket
 * 2. Inserts into `loan_documents` table with document_type = 'purchase_advice'
 * 3. Queues for GPU extraction in `document_processing_queue`
 *
 * Returns the document ID, storage path, and creation timestamp.
 */
export async function uploadPurchaseAdvice(params: PAUploadParams): Promise<PAUploadResult> {
  const { file, organizationId, encompassLoanId, userId, uploadContext, bankerReconId } = params;

  const documentId = `doc-${Date.now()}`;
  const storagePath = `${organizationId}/${encompassLoanId}/${documentId}.pdf`;

  // 1. Upload to storage
  await reserveLoanStorageObject(storagePath, encompassLoanId);
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from('loan-documents')
    .upload(storagePath, file);

  if (uploadError) {
    log.error('Failed to upload PA to storage', { encompassLoanId }, uploadError as unknown as Error);
    throw uploadError;
  }

  // 2. Insert into loan_documents
  const { data: docRecord, error: docError } = await supabase
    .from('loan_documents')
    .insert({
      organization_id: organizationId,
      encompass_loan_id: encompassLoanId,
      source_file: uploadData.path,
      original_filename: file.name,
      title: file.name,
      document_type: 'purchase_advice',
      uploaded_by: userId,
      status: 'processing',
      metadata: {
        upload_context: uploadContext
      },
      ...(bankerReconId ? { banker_recon_id: bankerReconId } : {})
    })
    .select('id, created_at')
    .single();

  if (docError) {
    log.error('Failed to insert PA loan_documents record', { encompassLoanId }, docError as unknown as Error);
    throw docError;
  }

  // 3. Queue for GPU extraction
  const { error: queueError } = await supabase
    .from('document_processing_queue')
    .insert({
      organization_id: organizationId,
      encompass_loan_id: encompassLoanId,
      storage_path: uploadData.path,
      status: 'pending',
      source_type: 'banker-recon',
      user_id: userId,
      document_id: docRecord.id
    });

  if (queueError) {
    log.error('Failed to queue PA for extraction', { encompassLoanId }, queueError as unknown as Error);
    throw queueError;
  }

  log.info('Purchase advice uploaded successfully', {
    docId: docRecord.id,
    storagePath: uploadData.path,
    encompassLoanId,
    uploadContext,
    bankerReconId: bankerReconId || null
  });

  return {
    docId: docRecord.id,
    storagePath: uploadData.path,
    createdAt: docRecord.created_at
  };
}
